import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  GreenContainer,
  GreenContainerQuestionWrapper,
  ClaimsContainer
} from '../../../components/application/defaultComponents'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import { useApplication } from '../../../context/application'
import { LeftBar } from 'src/components/basic/LeftBar'
import { FlexDiv } from 'src/components/application/formComponents/styles'
import StepBullet from 'src/components/basic/StepBullet'
import OptionButton from 'src/components/basic/OptionButton'
import ICOCItemForm from 'src/components/application/formComponents/ICOCItemForm'
import flagWithCheckImg from 'src/assets/applicationFormsImgs/flag_with_check.svg'
import { AttorneyData } from 'src/dtos/applicationData'
import { ValidationError } from 'yup'
import produce from 'immer'
import { generateRandomNumberId } from 'src/utils/math'
import { onlyICOCAttorneys } from 'src/utils/newApplications'
import { AttorneysICOCSchema } from 'src/yup/NewAppSchema'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

interface AttorneyDataWithId extends AttorneyData {
  id: number;
}

const ICOC = () => {
  /*
    0 - EC - 
    1 - EC - 
    2 - EC245 - YearStartedAsICOC
    3 - EC246 - HoursOnBill <---
    4 - EC247 - HoursBilledInFirstYear <---
    5 - EC248 - ActsIndependentlyFromFirm
    6 - EC249 - ActsIndependentlyFromFirmWhere
    7 - EC250 - HasInsuranceForIndependentPractice
    8 - EC251 - EffectivePolicyDateFrom
    9 - EC252 - EffectivePolicyDateTo
  */
  const newAppICOCErrorCodes = ['EC', 'EC', 'EC245', 'EC246', 'EC247', 'EC248', 'EC249', 'EC250', 'EC251', 'EC252']
  const [valErrors, setValErrors] = useState<any[]>()
  const { application } = useApplication()
  const [attorneys, setAttorneys] = useState<AttorneyDataWithId[]>(() => {
    const atts = onlyICOCAttorneys(application)

    return atts.map(att => ({ ...att, id: generateRandomNumberId() }))
  });
  
  const [hasICOCAttorneys, setHasICOCAttorneys] = useState(() => {
    if (!!application?.attorneysSection?.hasICOCAttorneys) {
      return {
        isVisible: !!application?.attorneysSection?.hasICOCAttorneys,
        value: application?.attorneysSection?.hasICOCAttorneys
      }
    }
    return { isVisible: false, value: false }
  });

  const [showErrors, setShowErrors] = useState(false);

  const isAllSet = useCallback(() => {
    return !hasICOCAttorneys.value;
  }, [hasICOCAttorneys.value]);

  const updateAttorneys = useCallback((index: number, data: Partial<AttorneyDataWithId>) => {
    setAttorneys(produce(draft => { draft[index] = { ...draft[index], ...data } }))
  }, []);

  const updatedApplicationData = () => {
    let updatedAttorneys = [...(application?.attorneysSection?.attorneys ?? [])]

    if (updatedAttorneys && updateAttorneys.length > 0) {
      updatedAttorneys = updatedAttorneys.map(att => {
        const icocAttorneyFound = attorneys
          .find(icoc =>
            icoc.firstName === att.firstName &&
            icoc.lastName === att.lastName &&
            icoc.barCardNumber === att.barCardNumber
          )


        if (!!icocAttorneyFound) {
          const { id, ...rest } = icocAttorneyFound

          return rest
        }

        return att
      })

      return {
        ...application,
        currentPage: window.location.pathname,
        attorneysSection: {
          ...application?.attorneysSection,
          hasICOCAttorneys: hasICOCAttorneys.value,
          attorneys: updatedAttorneys
        }
      }
    }

    return application
  }

  const validatePage = useMemo(() => {
    if (isAllSet()) return true;

    const validations = attorneys.map((attorney, index) => {
      try {
        AttorneysICOCSchema.validateSync(attorney, { abortEarly: false });
        return [];
      } catch (e) {
        const { errors } = e as ValidationError;

        const filteredErrors = errors.filter(err => err !== 'typeerror');

        return `${filteredErrors}:${index}`;
      }
    }).flat()
    setValErrors(validations);
    if (validations.length > 0) return false

    return true
  }, [attorneys, isAllSet])

  const scrollPageIfInvalid = () => {
    if (!(validatePage)) {
      for (let attorney of attorneys) {
        try {
          AttorneysICOCSchema.validateSync(attorney, { abortEarly: false })
        } catch (err) {
          const { errors } = err as ValidationError

          if (!(errors.length === 1 && (errors.includes('typeerror')))) {
            setShowErrors(true)
            
            document.getElementById(`${[attorney?.firstName, attorney?.lastName].join('').replaceAll(' ', '_')}__${errors[0]}`)?.scrollIntoView({ behavior: 'smooth' })
            break
          }
        }
      }
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Please fill out the following regarding independent contractors and of counsel attorneys.</DefaultTitle>
        <GreenContainer>
          <div className='green-container-content'>
            <h6>You’re being asked to fill out this supplemental information based on your answers to the following questions.</h6>
            <span>If this information is incorrect, please change your answers.</span>
            <div>
              <LeftBar />
              <GreenContainerQuestionWrapper>
                {hasICOCAttorneys.isVisible ? (
                  <div className='green-container-question-div'>
                    <StepBullet>2</StepBullet>
                    <FlexDiv dir='column' gap="24px">
                      <p>
                        Does any lawyer(s) act as an independent contractor (IC) and/or "Of Counsel" (OC) to your firm
                        or is listed as such on your firm's letterhead or website?
                      </p>
                      <FlexDiv gap="16px">
                        <OptionButton
                          checked={hasICOCAttorneys.value === true}
                          onChange={() => setHasICOCAttorneys(prev => ({ ...prev, value: true }))}
                          errorCode='EC309'
                        >
                          Yes
                        </OptionButton>
                        <OptionButton
                          checked={hasICOCAttorneys.value === false}
                          onChange={() => setHasICOCAttorneys(prev => ({ ...prev, value: false }))}
                          errorCode='EC309'
                        >
                          No
                        </OptionButton>
                      </FlexDiv>
                        <ValidationErrorMessage errorCode='EC309' />
                    </FlexDiv>
                  </div>
                ) : null}
              </GreenContainerQuestionWrapper>
            </div>
          </div>
        </GreenContainer>
        {isAllSet() ? (
          <GreenContainer>
            <div className='claim-all-set'>
              <div className='claim-all-set-icon'>
                <img src={flagWithCheckImg} alt="All Set" />
              </div>
              <div className='claim-all-set-message'>
                <h3>All Set!</h3>
                <h4>You no longer need to complete this section.</h4>
              </div>
            </div>
          </GreenContainer>
        ) : (
          <ClaimsContainer>
            <span className='claims-form-header-subtitle'>
              Please complete a separate supplement for each attorney who acts as "Of Counsel"
              of and "Independent Contractor" for your firm. If you do not see the attorney listed below, please go back to the Attorney section and add an attorney with the position of Of Counsel or Independent Contractor.
              <br />
              If you do not know, leave blank.
            </span>
            {attorneys.map((attorney, index) => (
              <ICOCItemForm
                key={attorney.id}
                attorney={attorney}
                attorneyIndex={attorney.id}
                setAttorney={data => updateAttorneys(index, data)}
                isIncorrect={!validatePage && showErrors}
                errorCodes={newAppICOCErrorCodes.map(e => `${e}:${index}`)}
                validationErrors={valErrors}
              />
            ))}
          </ClaimsContainer>
        )}
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/attorneys/relationships"
        isFormValid={validatePage}
        nextRoute="/insurance/last-insurances"
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={scrollPageIfInvalid}
      />
    </ApplicationBodyContainer>
  )
}

export default ICOC